import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {BackendForAppErrorCode} from '../../../bfa-api/models/backend-for-app-error-code';
import {
    AbortedByStampActionError,
    AiNotEnoughQuotaError,
    AiServicesNotEnabledError,
    ArchivationIsNotAuthorizedForDocumentError,
    ClickFinderNodeNotFoundError,
    ConditionNotFoundError,
    DocumentCurrentlyNotEditableError,
    DocumentCurrentlyNotStampableError,
    DocumentDeletedError,
    DocumentIsLockedByOtherUserError,
    DocumentIsNotLockedError,
    DocumentNameAlreadyExistsError,
    DocumentNotFoundError,
    IconNotFoundError,
    InvalidArgumentError,
    InvalidDocumentIdError,
    InvalidModelStateError,
    InvalidStampActionResponseError,
    InvalidTagValueError,
    LanguageCultureNotFoundError,
    MagnetNotFoundError,
    NoReadableDocumentsError,
    NotAuthorizedError,
    ReadonlyTagValueError,
    RequiredTagValueError,
    SearchQueryNotFoundError,
    SearchResultsNotFoundError,
    ServerMaintenanceError,
    StampedValueIsNotAllowedError,
    StampInputRequiredError,
    StampIsNotVisualError,
    StampNotFoundError,
    StampTagNotFoundError,
    StampWithDuplicatedMmsNotUsableError,
    StreamNotAvailableError,
    TagDefinitionNotFoundError,
    TagDefinitionVersionChangedError,
    TagNotFoundError,
    UserNotFoundError,
    VaultNotFoundError
} from '../../../errors/api-errors';
import {DateMappingService} from '../date-mapping/date-mapping.service';
import {StampRowType} from '../../../bfa-api/models';

@Injectable({
    providedIn: 'root'
})
export class ApiErrorMappingService {
    private readonly dateMappingService = inject(DateMappingService);

    public mapBfaError(error: unknown): Error {
        if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
            return error as Error;
        }

        let bfaErrorCode = error.error?.code as BackendForAppErrorCode;
        let jsonParsedBfaError;
        if (error.error) {
            try {
                if (!bfaErrorCode) {
                    jsonParsedBfaError = JSON.parse(error.error);
                    bfaErrorCode = jsonParsedBfaError?.code as BackendForAppErrorCode;
                } else {
                    jsonParsedBfaError = error.error;
                }
            } catch {
                return error;
            }
        }

        switch (bfaErrorCode) {
            case BackendForAppErrorCode.AbortedByStampAction:
                return new AbortedByStampActionError();
            case BackendForAppErrorCode.AiNotEnoughQuota:
                return new AiNotEnoughQuotaError();
            case BackendForAppErrorCode.AiServicesNotEnabled:
                return new AiServicesNotEnabledError();
            case BackendForAppErrorCode.ArchivationIsNotAuthorizedForDocument:
                return new ArchivationIsNotAuthorizedForDocumentError();
            case BackendForAppErrorCode.ClickFinderNodeNotFound:
                return new ClickFinderNodeNotFoundError();
            case BackendForAppErrorCode.ConditionNotFound:
                return new ConditionNotFoundError();
            case BackendForAppErrorCode.DocumentCurrentlyNotEditable:
                return new DocumentCurrentlyNotEditableError();
            case BackendForAppErrorCode.DocumentCurrentlyNotStampable:
                return new DocumentCurrentlyNotStampableError();
            case BackendForAppErrorCode.DocumentDeleted:
                return new DocumentDeletedError();
            case BackendForAppErrorCode.DocumentIsLockedByOtherUser: {
                const lockedSinceDateUTC = jsonParsedBfaError.details?.lockedSince;
                let date: Date | undefined = undefined;
                if (lockedSinceDateUTC) {
                    date = this.dateMappingService.mapStringToDate(lockedSinceDateUTC);
                }
                date ??= new Date(Date.now());

                return new DocumentIsLockedByOtherUserError(date,
                    jsonParsedBfaError.details?.lockedByUserId || '',
                    jsonParsedBfaError.details?.lockedByUserFullName || '');
            }
            case BackendForAppErrorCode.DocumentIsNotLocked:
                return new DocumentIsNotLockedError();
            case BackendForAppErrorCode.DocumentNameAlreadyExists:
                return new DocumentNameAlreadyExistsError();
            case BackendForAppErrorCode.DocumentNotFound:
                return new DocumentNotFoundError();
            case BackendForAppErrorCode.IconNotFound:
                return new IconNotFoundError();
            case BackendForAppErrorCode.InvalidArgument:
                return new InvalidArgumentError();
            case BackendForAppErrorCode.InvalidDocumentId:
                return new InvalidDocumentIdError();
            case BackendForAppErrorCode.InvalidModelState:
                return new InvalidModelStateError();
            case BackendForAppErrorCode.InvalidStampActionResponse:
                return new InvalidStampActionResponseError();
            case BackendForAppErrorCode.InvalidTagValue:
                return new InvalidTagValueError(jsonParsedBfaError.details?.tagId);
            case BackendForAppErrorCode.LanguageCultureNotFound:
                return new LanguageCultureNotFoundError();
            case BackendForAppErrorCode.MagnetNotFound:
                return new MagnetNotFoundError();
            case BackendForAppErrorCode.NoReadableDocuments:
                return new NoReadableDocumentsError();
            case BackendForAppErrorCode.NotAuthorized:
                return new NotAuthorizedError();
            case BackendForAppErrorCode.ReadonlyTagValue:
                return new ReadonlyTagValueError(jsonParsedBfaError.details?.tagId);
            case BackendForAppErrorCode.RequiredTagValue:
                return new RequiredTagValueError(jsonParsedBfaError.details?.tagId);
            case BackendForAppErrorCode.SearchQueryNotFound:
                return new SearchQueryNotFoundError();
            case BackendForAppErrorCode.SearchResultsNotFound:
                return new SearchResultsNotFoundError();
            case BackendForAppErrorCode.ServerMaintenance:
                return new ServerMaintenanceError();
            case BackendForAppErrorCode.StampedValueIsNotAllowed:
                return new StampedValueIsNotAllowedError(jsonParsedBfaError.details?.tagDefinitionId ?? '',
                    jsonParsedBfaError.details?.stampRowId ?? '',
                    jsonParsedBfaError.details?.stampRowType ?? StampRowType.TagDefinition);
            case BackendForAppErrorCode.StampInputRequired:
                return new StampInputRequiredError(jsonParsedBfaError.details?.tagDefinitionId ?? '',
                    jsonParsedBfaError.details?.stampRowId ?? '',
                    jsonParsedBfaError.details?.stampRowType ?? StampRowType.TagDefinition);
            case BackendForAppErrorCode.StampIsNotVisual:
                return new StampIsNotVisualError();
            case BackendForAppErrorCode.StampNotFound:
                return new StampNotFoundError();
            case BackendForAppErrorCode.StampTagNotFound:
                return new StampTagNotFoundError();
            case BackendForAppErrorCode.StampWithDuplicatedMmsNotUsable:
                return new StampWithDuplicatedMmsNotUsableError();
            case BackendForAppErrorCode.StreamNotAvailable:
                return new StreamNotAvailableError();
            case BackendForAppErrorCode.TagDefinitionNotFound:
                return new TagDefinitionNotFoundError();
            case BackendForAppErrorCode.TagDefinitionVersionChanged:
                return new TagDefinitionVersionChangedError();
            case BackendForAppErrorCode.TagNotFound:
                return new TagNotFoundError();
            case BackendForAppErrorCode.UserNotFound:
                return new UserNotFoundError();
            case BackendForAppErrorCode.VaultNotFound:
                return new VaultNotFoundError();
            case BackendForAppErrorCode.None:
            default:
                return error;
        }
    }
}
